/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

interface useIndexProps {
	/**
	 * This should be the Enum holding the definition of the tabs.
	 * You MUST pass the Enum type itself.
	 */
	tabEnum: any;

	/**
	 * An array holding the cache key for each tab
	 * Order should align with the order of the enum defined above
	 */
	tabCacheKeys: Array<string>;

	/**
	 * The default index to show on page load, in cases where some tabs will be shown
	 * or hidden dynamically such as user roles
	 */
	defaultTab?: number;

	/**
	 * Do you have custom functionality for obtaining the tab label?
	 * This function will be called with a parameter for the current tab's index
	 * @param args Any
	 * @returns String label of the tab
	 */
	getTabLabelOverride?: (...args: Array<any>) => string;

	/**
	 * Do you have custom functionality for obtaining the tab index?
	 * This function will be called with a parameter for the current tab's label
	 * @param args Any
	 * @returns Index of the tab
	 */
	getTabIndexOverride?: (...args: Array<any>) => number;

	/**
	 * Do you have custom functionality for obtaining the tab cache key?
	 * This function will be called with a parameter for the current tab's index
	 * @param args Any
	 * @returns Selected tab's cache key
	 */
	getTabCacheKeyOverride?: (...args: Array<any>) => string;
}

const useIndex = (props: useIndexProps) => {
	// Props
	const {
		tabEnum,
		tabCacheKeys,
		defaultTab = 0,
		getTabLabelOverride,
		getTabIndexOverride,
		getTabCacheKeyOverride
	} = props;

	// State
	const [activeTab, setActiveTab] = useState<typeof tabEnum>(defaultTab);

	// Hooks
	const { tab } = useParams();

	/**
	 * Helper function to determine if a function is callable
	 * Does not soothe eslint undefined checks
	 * @param func The function to check
	 * @returns Boolean
	 */
	const isFunctionCallable = (func?: (...args: Array<any>) => any): boolean => !!func && typeof func === "function";

	/**
	 * Given an index, returns the label of the current tab.
	 * Default functionality returns enum name in lowercase
	 * If custom functionality provided, will call that instead
	 * @param index Index of the current active tab
	 * @returns String label of active tab
	 */
	const getTabLabel = (index: number): string => {
		if (getTabLabelOverride && isFunctionCallable(getTabLabelOverride)) return getTabLabelOverride(index);
		else return tabEnum[index].toLowerCase() as string;
	};

	/**
	 * Given a string, returns the index of the current tab.
	 * Default functionality will search the enum object for the provided string
	 * If custom functionality provided, will call that instead
	 * @param label String label of the current active tab
	 * @returns Index of active tab
	 */
	const getTabIndex = (label: string): number => {
		if (getTabIndexOverride && isFunctionCallable(getTabIndexOverride)) return getTabIndexOverride(label);
		else
			return Object.keys(tabEnum)
				.filter((key: string) => isNaN(parseInt(key)))
				.map((key: string) => key.toLowerCase())
				.indexOf(label.toLowerCase());
	};

	/**
	 * Given an index, returns the cache key for the current tab.
	 * Default functionality will use the same index as the current tab's enum
	 * e.g. - If enum index 1 is active, will return index 1 from cache key array
	 * If custom functionality provided, will call that instead
	 * @param index Index of active tab
	 * @returns Cache key for active tab
	 */
	const getTabCacheKey = (index: number): string => {
		try {
			if (getTabCacheKeyOverride && isFunctionCallable(getTabCacheKeyOverride)) return getTabCacheKeyOverride(index);
			else return tabCacheKeys[index];
		} catch (error) {
			console.error(`Tried fetching cache key for unknown tab index: (${index})`, error);
			return "";
		}
	};

	useEffect(() => {
		// If the current page loads with a tab, set it
		if (tab) setActiveTab(getTabIndex(tab));
	}, [tab]);

	return {
		// Coerce this to a number for type safety and to avoid eslint problems
		activeTab: activeTab as number,

		setActiveTab,
		getTabLabel,
		getTabIndex,
		getTabCacheKey
	};
};

export default useIndex;
